/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { core, dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { AccountCreation, AuthRequest, DeviceState, ResetTokenRequest } = protos;

// Returns a promise in order to chain logic after the active business is set.
export function setActive(businessId: number): Promise<unknown> {
  return Promise.resolve({
    type: AppConstants.AUTHENTICATION_SET_ACTIVE_DONE,
    payload: businessId,
  });
}

export function createAccountIfAuthenticated({
  email,
  password,
  token,
  firstName,
  lastName,
}: core.IAccountCreation): ApiAction {
  return {
    TockApi: {
      postProto: '/patron/signup-token',
      request: AccountCreation.fromObject({
        firstName,
        lastName,
        email,
        password,
        token,
        authProvider: 'PASSWORD',
        context: 'SELF_ONBOARDING',
      }),
      type: AppConstants.CREATE_ACCOUNT,
    },
  };
}

export function logout(): BaseTockAction {
  return {
    type: AppConstants.AUTHENTICATION_CLEARED_DONE,
  };
}

export function updateAuthenticationRoles(currentBusinessId?: number): ApiAction {
  return {
    TockApi: {
      getProto:
        '/authenticate/roles' +
        (currentBusinessId ? `?currentBusinessId=${currentBusinessId}` : ''),
      type: AppConstants.AUTHENTICATION_ROLES_UPDATE,
    },
  };
}

export function authenticate(email: string, password: string): ApiAction {
  const request = AuthRequest.fromObject({
    email,
    password,
    type: 'ADMIN_UI',
  });

  return {
    TockApi: {
      postProto: '/authenticate',
      request,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function authenticateForSelfOnboarding(email: string, password: string): ApiAction {
  const request = AuthRequest.fromObject({
    email,
    password,
    type: 'SELF_ONBOARDING_UI',
  });

  return {
    TockApi: {
      postProto: '/authenticate',
      request,
      type: AppConstants.AUTHENTICATION_RESPONSE_SELF_ONBOARDING,
    },
  };
}

export function authenticateWithTokenForSelfOnboarding(token: string): ApiAction {
  const request = AuthRequest.fromObject({
    accessToken: token,
    type: 'SELF_ONBOARDING_UI',
  });
  return {
    TockApi: {
      postProto: `/patron/activate/auth`,
      type: AppConstants.AUTHENTICATION_RESPONSE_SELF_ONBOARDING,
      request,
    },
  };
}

export function getAuthenticationMethods(email: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/authenticate/methods?email=${encodeURIComponent(email)}`,
      type: AppConstants.AUTHENTICATION_METHODS,
    },
  };
}

export function resetPassword(email: string): ApiAction {
  return {
    TockApi: {
      getProto: `/password/reset?type=admin&email=${encodeURIComponent(email)}`,
      type: AppConstants.NULL,
      toast: `We've sent password reset instructions to ${email}. Check your inbox.`,
    },
  };
}

export function resetPasswordWithToken(requestJSON: any): ApiAction {
  const request = ResetTokenRequest.fromObject(requestJSON);
  return {
    TockApi: {
      postProto: '/password/reset/token',
      request,
      type: AppConstants.NULL,
      toast: 'Your password has been reset. You can now log in with your new password.',
    },
  };
}

export function refreshAuthFromStorage(): BaseTockAction {
  return {
    type: AppConstants.AUTH_REFRESH_FROM_STORAGE,
  };
}

export function resendActivationEmail(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron/activate/resend?context=SELF_ONBOARDING',
      type: AppConstants.NULL,
      toast: 'Another activation email has been sent to your inbox',
    },
  };
}

export function authenticateWithInviteToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/role/dashboard/auth?token=${encodeURIComponent(token)}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function createAccount(email: string, password: string): ApiAction {
  const request = AccountCreation.fromObject({
    email,
    password,
    authProvider: 'PASSWORD',
    context: 'ADMIN_UI',
  });
  return {
    TockApi: {
      postProto: '/patron',
      request,
      type: AppConstants.CREATE_ACCOUNT,
    },
  };
}

export function createAccountWithToken(
  email: string,
  password: string,
  token: string,
  patronDetails?: any
): ApiAction {
  const request = AccountCreation.fromObject({
    email,
    password,
    token,
    authProvider: 'PASSWORD',
    context: 'ADMIN_UI',
    ..._.pick(patronDetails || {}, ['firstName', 'lastName', 'phone', 'zipCode']),
  });
  return {
    TockApi: {
      postProto: '/patron/signup-token',
      request,
      type: AppConstants.CREATE_ACCOUNT,
    },
  };
}

export function createAccountWithProfile({
  email,
  password,
  firstName,
  lastName,
  authProvider,
  context,
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  authProvider: string;
  context: string;
}): ApiAction {
  return {
    TockApi: {
      postProto: '/patron',
      request: AccountCreation.fromObject({
        email,
        password,
        firstName,
        lastName,
        authProvider,
        context,
      }),
      type: AppConstants.CREATE_ACCOUNT,
      onError: {
        2010: '', // Account already exists
        defaultToOriginal: true,
      },
    },
  };
}

export function fetchLoggedInProfile(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron',
      type: AppConstants.PATRON_FETCH_PROFILE,
    },
  };
}

export function updateProfile(profile: any): ApiAction {
  return {
    TockApi: {
      putProto: '/patron',
      request: profile,
      type: AppConstants.PATRON_SET_PROFILE,
    },
  };
}

export function setOverrideLockout(): Promise<unknown> {
  return Promise.resolve({
    type: AppConstants.OVERRIDE_LOCKOUT,
    payload: {},
  });
}

export function sendTelemetry(deviceState: dashboard.IDeviceState): ApiAction {
  return {
    TockApi: {
      postProto: '/telemetry/device/state',
      request: DeviceState.fromObject(deviceState),
      type: AppConstants.NULL,
    },
  };
}
